import React, { useState, useRef, useEffect, useCallback } from 'react';
import { User, Camera, Edit3, AtSign, Info, Shield, Image as ImageIcon, Save, Trophy, Star, Link2, Copy, FileText, Wrench, Lightbulb, Trash2 } from 'lucide-react';
import './Profile.css';
import { compressImage } from '../utils/image';
import useTelegramMainButton from '../hooks/useTelegramMainButton';

import MyLink from './MyLink';

const Profile = ({ user, onUserUpdate, vipSettings }) => {
  const [formData, setFormData] = useState({
    nickname: '',
    customUsername: '',
    bio: '',
    customNameColor: '',
    gender: '',
    age: '',
    settings: {
      privacy: 'online',
      allowMedia: true
    }
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Modals & Ticketing States
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [supportMsg, setSupportMsg] = useState('');
  const [suggestionMsg, setSuggestionMsg] = useState('');
  const [submittingSupport, setSubmittingSupport] = useState(false);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  const [userTickets, setUserTickets] = useState([]);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  const getVipRemainingTime = () => {
    if (!user?.vipExpiresAt) return null;
    const expires = new Date(user.vipExpiresAt);
    const diff = expires.getTime() - new Date().getTime();
    if (diff <= 0) return null;
    const remainingDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (remainingDays > 1) {
        return { value: remainingDays, unit: 'يوم', totalHours: Math.ceil(diff / (1000 * 60 * 60)) };
    } else {
        const remainingHours = Math.ceil(diff / (1000 * 60 * 60));
        return { value: remainingHours, unit: 'ساعة', totalHours: remainingHours };
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/users/${userId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        alert('✨ تم حذف حسابك بنجاح. سنشتاق إليك! ❤️');
        localStorage.clear();
        if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.close();
        } else {
            window.location.reload();
        }
      } else {
        const data = await res.json();
        alert(data.error || 'فشل حذف الحساب. يرجى المحاولة لاحقاً.');
      }
    } catch (err) {
      console.error('Delete account error:', err);
      alert('حدث خطأ في الاتصال بالخادم.');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const fetchUserSupport = useCallback(async () => {
    if (!user) return;
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch(`/api/support/user/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setUserTickets(data.tickets || []);
        setUserSuggestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Error fetching user support:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchUserSupport();
  }, [fetchUserSupport]);

  const handleSendSupport = async (e) => {
    e.preventDefault();
    if (!supportMsg.trim()) return;
    setSubmittingSupport(true);
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch('/api/support/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, message: supportMsg })
      });
      if (res.ok) {
        alert('✨ تم إرسال تذكرتك بنجاح وسيتواصل معك الدعم الفني قريباً!');
        setSupportMsg('');
        setShowSupportModal(false);
        fetchUserSupport();
      } else {
        alert('❌ فشل إرسال التذكرة. يرجى المحاولة لاحقاً.');
      }
    } catch (err) {
      console.error(err);
      alert('⚠️ خطأ في الاتصال بالسيرفر.');
    } finally {
      setSubmittingSupport(false);
    }
  };

  const handleSendSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestionMsg.trim()) return;
    setSubmittingSuggestion(true);
    const userId = user.id || user.telegramId;
    try {
      const res = await fetch('/api/support/suggestion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, suggestion: suggestionMsg })
      });
      if (res.ok) {
        alert('✨ تم تقديم اقتراحك بنجاح! نشكرك على مساهمتك في تطوير تطبيق سرّك. ❤️');
        setSuggestionMsg('');
        setShowSuggestionModal(false);
        fetchUserSupport();
      } else {
        alert('❌ فشل إرسال الاقتراح.');
      }
    } catch (err) {
      console.error(err);
      alert('⚠️ خطأ في الاتصال بالسيرفر.');
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const handleSendTicketReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeTicket) return;
    setSubmittingReply(true);
    try {
      const res = await fetch(`/api/support/ticket/${activeTicket._id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sender: 'user', text: replyText })
      });
      if (res.ok) {
        const data = await res.json();
        const updatedTicket = {
          ...activeTicket,
          status: 'pending',
          messages: [...(activeTicket.messages || []), data.messageObj]
        };
        setActiveTicket(updatedTicket);
        setReplyText('');
        fetchUserSupport();
      } else {
        alert('❌ فشل إرسال الرد.');
      }
    } catch (err) {
      console.error(err);
      alert('⚠️ خطأ في الاتصال بالسيرفر.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('هل تريد إغلاق هذه التذكرة ووسمها كمكتملة؟')) return;
    try {
      const res = await fetch(`/api/support/ticket/${ticketId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'closed' })
      });
      if (res.ok) {
        if (activeTicket && activeTicket._id === ticketId) {
          setActiveTicket({ ...activeTicket, status: 'closed' });
        }
        fetchUserSupport();
      } else {
        alert('❌ فشل تحديث حالة التذكرة.');
      }
    } catch (err) {
      console.error(err);
      alert('⚠️ خطأ في الاتصال بالسيرفر.');
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        nickname: user.nickname || '',
        customUsername: user.customUsername || '',
        bio: user.bio || '',
        customNameColor: user.customNameColor || '#eab308',
        gender: user.gender || '',
        age: user.age || '',
        settings: user.settings || { privacy: 'online', allowMedia: true }
      });
      setAvatarUrl(user.avatarUrl);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [name]: value
      }
    }));
  };

  const handleAvatarClick = () => {
    if (!uploading) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // ضغط الصورة قبل الرفع
      file = await compressImage(file, 400, 0.7);

      const userId = user.id || user.telegramId;
      const data = new FormData();
      data.append('avatar', file);

      const response = await fetch(`/api/users/${userId}/avatar`, {
        method: 'POST',
        body: data
      });

      if (response.ok) {
        const result = await response.json();
        setAvatarUrl(result.user.avatarUrl);
        if (onUserUpdate) onUserUpdate(result.user);
      } else {
        alert('فشل في رفع الصورة');
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('حدث خطأ في الاتصال');
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUsername = (username) => {
    if (!username) return;
    navigator.clipboard.writeText(`@${username}`);
    alert(`تم نسخ المعرف @${username} بنجاح!`);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Frontend Validations
    if (formData.customUsername && formData.customUsername.trim() !== '') {
      const username = formData.customUsername.trim();
      const minLength = user.isAdmin ? 3 : 4;
      if (username.length < minLength) {
        alert(`اسم المستخدم (اليوزر) يجب أن يكون على الأقل ${minLength} أحرف.`);
        return;
      }
    }

    if (formData.age !== undefined && formData.age !== null && formData.age !== '') {
      const ageNum = parseInt(formData.age, 10);
      if (isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
        alert('العمر يجب أن يكون بين 1 و 120 عاماً.');
        return;
      }
    }

    setLoading(true);
    const userId = user?.id || user?.telegramId;

    try {
      const response = await fetch(`/api/users/${userId}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        if (onUserUpdate) onUserUpdate(data.user);
        alert('تم حفظ الملف الشخصي بنجاح!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'حدث خطأ أثناء حفظ البيانات.');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('حدث خطأ بالاتصال.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeVip = async () => {
      setPaymentLoading(true);
      try {
          const response = await fetch('/api/payment/stars/invoice', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: user.id || user.telegramId })
          });

          const data = await response.json();
          if (data.ok && data.invoiceLink) {
              const tg = window.Telegram?.WebApp;
              if (tg) {
                  tg.openInvoice(data.invoiceLink, (status) => {
                      if (status === 'paid') {
                          alert('🎉 مبارك! تم الدفع بنجاح وتفعيل اشتراك VIP الشهري 🌟');
                          window.location.reload();
                      } else if (status === 'failed') {
                          alert('❌ فشلت عملية الدفع بالنجوم.');
                      } else {
                          alert('⚠️ تم إلغاء عملية الدفع.');
                      }
                  });
              } else {
                  alert(`يرجى فتح البوت داخل تيليجرام لإتمام الدفع.\nرابط الفاتورة: ${data.invoiceLink}`);
              }
          } else {
              alert('فشل توليد فاتورة الدفع: ' + (data.error || 'خطأ غير معروف'));
          }
      } catch (err) {
          console.error(err);
          alert('حدث خطأ في الاتصال بالسيرفر أثناء توليد الفاتورة.');
      } finally {
          setPaymentLoading(false);
      }
  };

  // حسابات المستوى ونقاط الخبرة
  const currentLevel = user?.level || 1;
  const currentXp = user?.xp || 0;
  const xpForCurrentLevel = Math.pow((currentLevel - 1) * 2, 2);
  const xpForNextLevel = Math.pow(currentLevel * 2, 2);
  const xpIntoCurrentLevel = currentXp - xpForCurrentLevel;
  const xpNeededForNext = xpForNextLevel - xpForCurrentLevel;
  const xpProgress = xpNeededForNext > 0 ? (xpIntoCurrentLevel / xpNeededForNext) * 100 : 0;

  const isNativeMainActive = useTelegramMainButton({
    text: "حفظ التغييرات",
    onClick: handleSubmit,
    isVisible: true,
    isEnabled: !loading && !uploading,
    showProgress: loading
  });

  return (
    <div className="profile-main-container">
      <h2 className="page-title text-center mb-4">الملف الشخصي</h2>

      {/* قسم المستوى ونقاط الخبرة */}
      <div className="level-card">
        <div className="level-header">
            <div className="level-badge-modern">
                <Trophy size={20} className="gold-icon" />
                <span>مستوى {currentLevel}</span>
            </div>
            <span className="xp-text-modern"><Star size={14} className="gold-icon" /> {currentXp} / {xpForNextLevel} XP</span>
        </div>
        <div className="xp-progress-container-modern">
          <div className="xp-progress-bar-modern" style={{ width: `${Math.min(xpProgress, 100)}%` }}></div>
        </div>
        <p className="level-hint">تفاعل أكثر في المحادثات لزيادة مستواك!</p>
      </div>

      {/* قسم الـ VIP المذهب والأنيق */}
      {user?.isVip ? (() => {
          const remaining = getVipRemainingTime();
          const isCloseToExpiration = remaining && remaining.totalHours <= 72;
          return (
              <div className="vip-status-card-premium" style={isCloseToExpiration ? { border: '1px solid rgba(239, 68, 68, 0.4)', background: 'linear-gradient(135deg, rgba(30, 17, 17, 0.9), rgba(17, 24, 39, 0.95))' } : {}}>
                  <div className="vip-badge-shining">
                      <Star size={24} className="gold-star-spin" />
                      <span>اشتراك VIP شهري نشط</span>
                  </div>
                  {remaining && (
                      <div className="vip-remaining-badge" style={{ marginTop: '10px', fontSize: '0.9rem', color: isCloseToExpiration ? '#f87171' : '#fbbf24', fontWeight: 'bold' }}>
                          ⏳ متبقي على انتهاء الاشتراك: {remaining.value} {remaining.unit}
                      </div>
                  )}
                  {isCloseToExpiration && (
                      <div className="vip-renewal-alert" style={{ marginTop: '12px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', padding: '10px', textAlign: 'right' }}>
                          <p style={{ color: '#fca5a5', fontSize: '0.85rem', margin: 0, fontWeight: 'bold' }}>⚠️ سينتهي اشتراكك قريباً! يرجى التجديد لتجنب فقدان المزايا الحصرية.</p>
                          <button type="button" className="btn-pay-stars-premium" onClick={() => setShowVipModal(true)} style={{ marginTop: '8px', padding: '6px 12px', fontSize: '0.85rem', width: 'auto', background: '#eab308', color: '#1e1b4b' }}>
                              تجديد الاشتراك الآن 🌟
                          </button>
                      </div>
                  )}
                  <p className="vip-status-desc" style={{ marginTop: '12px' }}>تتمتع الآن بكامل المزايا الذهبية. اختر لون اسمك المميز:</p>
                  <div className="color-pickers-container">
                      {['#eab308', '#ec4899', '#3b82f6', '#10b981', '#a855f7', '#f43f5e'].map(color => (
                          <button
                              key={color}
                              type="button"
                              className={`color-dot-btn ${formData.customNameColor === color ? 'active' : ''}`}
                              style={{ backgroundColor: color }}
                              onClick={() => {
                                  const updatedFormData = { ...formData, customNameColor: color };
                                  setFormData(updatedFormData);
                                  // الحفظ الفوري في قاعدة البيانات
                                  fetch(`/api/users/${user.id || user.telegramId}/profile`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify(updatedFormData)
                                  }).then(res => {
                                      if (res.ok) return res.json();
                                  }).then(data => {
                                      if (data && onUserUpdate) onUserUpdate(data.user);
                                  });
                              }}
                          />
                      ))}
                  </div>
              </div>
          );
      })() : (
          <div className="vip-upgrade-card-premium" onClick={() => setShowVipModal(true)}>
              <div className="vip-upgrade-header">
                  <div className="vip-glow-title">
                      <Star size={20} className="gold-star-bounce" />
                      <span>الترقية إلى VIP</span>
                  </div>
                  <span className="stars-price-tag" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {vipSettings?.vipStarPrice || 50} <Star size={14} className="text-yellow-500 fill-yellow-500" style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                  </span>
              </div>
              <p className="vip-upgrade-intro">انقر لعرض المزايا الحصرية والترقية عبر نجوم تيليجرام فورياً!</p>
              <button type="button" className="btn-vip-action-glow">استكشف المزايا واشترك الآن</button>
          </div>
      )}

      {/* قسم رابطي الشخصي */}
      <MyLink user={user} />
      {/* قسم الصورة الشخصية */}
      <div className="avatar-section-modern">
        <div className={`avatar-wrapper ${uploading ? 'uploading' : ''}`} onClick={handleAvatarClick}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="avatar-image" />
          ) : (
            <User size={48} className="text-gray-400" />
          )}
          
          {/* الطبقة الشفافة التي تظهر عند التمرير */}
          <div className="avatar-overlay">
             {uploading ? <span className="loader-spinner"></span> : <Camera size={28} />}
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <p className="avatar-hint">اضغط على الصورة لتغييرها</p>
      </div>

      {/* نموذج البيانات والإعدادات */}
      <form className="profile-form-modern" onSubmit={handleSubmit}>
        
        <div className="form-group">
          <label>الاسم المستعار (يظهر للآخرين)</label>
          <div className="input-with-icon">
             <Edit3 size={18} className="input-icon" />
             <input
                type="text"
                name="nickname"
                value={formData.nickname}
                onChange={handleInputChange}
                maxLength={20}
                placeholder="اسمك المستعار"
                style={{ color: user.isVip && user.customNameColor ? user.customNameColor : 'inherit' }}
                className={user.isVip ? 'vip-glow-text' : ''}
             />
          </div>
        </div>

        <div className="form-group">
          <label>معرف المستخدم (Username)</label>
          <div className="username-input-wrapper">
             <AtSign size={18} className="input-icon" />
             <input
                type="text"
                name="customUsername"
                value={formData.customUsername}
                onChange={handleInputChange}
                placeholder="username"
             />
          </div>
          {user?.isVip && formData.customUsername && (
            <div className="vip-username-frame-container">
              <span className="vip-username-label">مظهر معرف VIP الفاخر الخاص بك (اضغط للنسخ):</span>
              <div className="vip-username-frame" onClick={() => handleCopyUsername(formData.customUsername)}>
                <span className="crystal-icon">✦</span>
                <span className="vip-username-text">@{formData.customUsername}</span>
                <span className="crystal-icon">✦</span>
                <button type="button" className="copy-username-btn" aria-label="نسخ المعرف">
                  <Copy size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* حقول الجنس والعمر المضافة */}
        <div className="form-row-two-col">
          <div className="form-group">
            <label>الجنس</label>
            <div className="gender-selector-container">
              <button
                type="button"
                className={`gender-btn male-btn ${formData.gender === 'ذكر' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, gender: 'ذكر' }))}
              >
                <span>ذكر ♂</span>
              </button>
              <button
                type="button"
                className={`gender-btn female-btn ${formData.gender === 'أنثى' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, gender: 'أنثى' }))}
              >
                <span>أنثى ♀</span>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>العمر</label>
            <div className="input-with-icon">
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                min="1"
                max="120"
                placeholder="أدخل عمرك"
                style={{ textAlign: 'right' }}
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>نبذة عنك (Bio)</label>
          <div className="input-with-icon textarea-icon">
             <Info size={18} className="input-icon" />
             <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                maxLength={150}
                placeholder="اكتب نبذة قصيرة عنك..."
             ></textarea>
          </div>
        </div>

        {/* إعدادات الخصوصية */}
        <div className="settings-card">
          <h3 className="settings-title"><Shield size={18} /> إعدادات الخصوصية</h3>

          <div className="setting-item">
            <div className="setting-info">
                <label>حالة الظهور</label>
                <span className="setting-desc">من يمكنه رؤية اتصالك بالإنترنت</span>
            </div>
            <div className="custom-select-wrapper">
                <select
                value={formData.settings.privacy}
                onChange={(e) => handleSettingChange('privacy', e.target.value)}
                >
                <option value="online">متصل (للجميع)</option>
                <option value="lastSeen">آخر ظهور فقط</option>
                <option value="hidden">مخفي تماماً</option>
                </select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
                <label>استقبال الوسائط</label>
                <span className="setting-desc">السماح باستقبال الصور والملفات</span>
            </div>
            <label className="modern-switch">
              <input
                type="checkbox"
                checked={formData.settings.allowMedia}
                onChange={(e) => handleSettingChange('allowMedia', e.target.checked)}
              />
              <span className="modern-slider"></span>
            </label>
          </div>
        </div>

        {!isNativeMainActive && (
          <button type="submit" className="btn-save-profile" disabled={loading || uploading}>
            {loading ? 'جاري الحفظ...' : <><Save size={20} /> حفظ التعديلات</>}
          </button>
        )}
      </form>

      {/* نافذة عرض مزايا VIP وإتمام الدفع بالنجوم */}
      {showVipModal && (
          <div className="vip-modal-overlay">
              <div className="vip-modal-content">
                  <div className="vip-modal-header">
                      <div className="vip-modal-title">
                          <Star size={22} color="#eab308" className="gold-star-spin" />
                          <h3>الترقية الذهبية VIP</h3>
                      </div>
                      <button type="button" className="vip-modal-close" onClick={() => setShowVipModal(false)}>×</button>
                  </div>
                  
                  <div className="vip-modal-body">
                      <p className="vip-modal-intro">امتلك الميزات الفائقة وتميز بين آلاف المستخدمين باشتراك شهري متجدد:</p>
                      
                      <div className="vip-perks-list-premium">
                          {(vipSettings?.vipPerks && vipSettings.vipPerks.length > 0 ? vipSettings.vipPerks : [
                              "اسم مميز باللون الذهبي البراق 🌟",
                              "شارة VIP خاصة تظهر بجانب اسمك ✨",
                              "مطابقات غير محدودة بدون فترات انتظار 🚀",
                              "تصفح التطبيق بالكامل بدون أي إعلانات 🛡️"
                          ]).map((perk, idx) => (
                              <div key={idx} className="vip-perk-item-modern">
                                  <div className="perk-bullet">✦</div>
                                  <span className="perk-text">{perk}</span>
                              </div>
                          ))}
                      </div>

                      <div className="vip-payment-box">
                          <div className="vip-price-display">
                              <span className="price-label">السعر:</span>
                              <span className="price-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{vipSettings?.vipStarPrice || 50} <Star size={16} className="text-yellow-500 fill-yellow-500" /> <span className="star-icon-text">نجمة</span></span>
                          </div>
                          
                          <button 
                              type="button" 
                              className="btn-pay-stars-premium" 
                              onClick={handleUpgradeVip}
                              disabled={paymentLoading}
                          >
                              {paymentLoading ? 'جاري الاتصال بتيليجرام...' : 'اشترك الآن بالنجوم'}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
      <div className="profile-support-section" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
        <h3 className="settings-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '14px' }}><Shield size={18} /> الدعم والمساعدة</h3>
        <div className="support-buttons-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
          <button type="button" className="btn-vip-action-glow" onClick={() => setShowPrivacyModal(true)} style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', color: '#fff', borderRadius: '12px', padding: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: 'none' }}>
             <FileText size={16} /> سياسة الخصوصية والاستخدام
          </button>
          <button type="button" className="btn-vip-action-glow" onClick={() => setShowSupportModal(true)} style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', color: '#fff', borderRadius: '12px', padding: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: 'none' }}>
             <Wrench size={16} /> التواصل مع الدعم الفني
          </button>
          <button type="button" className="btn-vip-action-glow" onClick={() => setShowSuggestionModal(true)} style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)', color: '#fff', borderRadius: '12px', padding: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: 'none' }}>
             <Lightbulb size={16} /> اقتراح ميزة جديدة
          </button>
          <button type="button" className="btn-vip-action-glow" onClick={() => setShowDeleteModal(true)} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#f87171', borderRadius: '12px', padding: '12px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: 'none', marginTop: '10px' }}>
             <Trash2 size={16} /> حذف الحساب نهائياً
          </button>
        </div>

        {(userTickets.length > 0 || userSuggestions.length > 0) && (
          <div className="user-tickets-list" style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <h4 style={{ fontSize: '0.92rem', color: '#cbd5e1', marginBottom: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>🎫 طلباتك واقتراحاتك السابقة</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {userTickets.map(t => (
                <div key={t._id} onClick={() => setActiveTicket(t)} style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '12px', padding: '12px 14px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
                    <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: '500', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1, overflow: 'hidden' }}>🎫 {t.message}</span>
                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{new Date(t.createdAt).toLocaleDateString('ar-SA')}</span>
                  </div>
                  <span className={`status-badge-pill ${t.status}`} style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: '20px', fontWeight: 'bold', display: 'inline-block', minWidth: '76px', textAlign: 'center' }}>
                    {t.status === 'replied' ? '✅ تم الرد' : t.status === 'closed' ? '🔒 مغلقة' : '⏳ قيد الانتظار'}
                  </span>
                </div>
              ))}
              {userSuggestions.map(s => (
                <div key={s._id} style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: '12px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
                    <span style={{ fontSize: '0.85rem', color: '#fff', fontWeight: '500', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1, overflow: 'hidden' }}>💡 {s.suggestion}</span>
                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{new Date(s.createdAt).toLocaleDateString('ar-SA')}</span>
                  </div>
                  <span className={`status-badge-pill ${s.status}`} style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: '20px', fontWeight: 'bold', display: 'inline-block', minWidth: '76px', textAlign: 'center' }}>
                    {s.status === 'replied' ? '✅ تم الرد' : '⏳ قيد المراجعة'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* مودال سياسة الخصوصية */}
      {showPrivacyModal && (
        <div className="vip-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="vip-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="vip-modal-header">
              <div className="vip-modal-title">
                <FileText size={20} color="#10b981" />
                <h3>سياسة الخصوصية والاستخدام</h3>
              </div>
              <button type="button" className="vip-modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
            </div>
            <div className="vip-modal-body" style={{ textAlign: 'right', fontSize: '0.88rem', lineHeight: '1.6', color: '#cbd5e1' }}>
              <p>نحن نهتم بخصوصيتك ونلتزم بحمايتها بالكامل. يرجى مراجعة بنود الخصوصية التالية:</p>
              <ul style={{ paddingRight: '20px', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                <li><b>جمع البيانات:</b> نقوم بجمع معلومات حسابك الأساسية من تيليجرام (الاسم، المعرف، وصورة الملف الشخصي إن وجدت) لإنشاء حسابك وتخصيصه.</li>
                <li><b>استخدام البيانات:</b> يتم استخدام الاسم المستعار، والعمر، والجنس، والنبذة الشخصية لتسهيل ميزة المطابقة والدردشة مع المستخدمين الآخرين.</li>
                <li><b>خصوصية الدردشات:</b> جميع الرسائل والوسائط المتبادلة في الدردشات الخاصة تكون مشفرة وآمنة تماماً ولا يمكن لأي طرف ثالث الاطلاع عليها.</li>
                <li><b>حقوق المستخدمين:</b> يمكنك تعديل معلوماتك الشخصية أو إخفاء حالة اتصالك بالإنترنت في أي وقت من خلال إعدادات الخصوصية المتاحة في ملفك الشخصي.</li>
              </ul>
              <p style={{ marginTop: '12px' }}>نحن مستمرون في تحسين خدماتنا لضمان توفير بيئة دردشة آمنة وموثوقة لجميع أعضاء سرّك. ❤️</p>
            </div>
          </div>
        </div>
      )}

      {/* مودال الدعم الفني */}
      {showSupportModal && (
        <div className="vip-modal-overlay" onClick={() => setShowSupportModal(false)}>
          <div className="vip-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="vip-modal-header">
              <div className="vip-modal-title">
                <Wrench size={20} color="#3b82f6" />
                <h3>الدعم الفني والتبليغ</h3>
              </div>
              <button type="button" className="vip-modal-close" onClick={() => setShowSupportModal(false)}>×</button>
            </div>
            <form onSubmit={handleSendSupport} className="vip-modal-body" style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '12px' }}>هل واجهت مشكلة في التطبيق أو لديك استفسار؟ اكتب رسالتك وسيرد عليك أحد مسؤولي الدعم الفني عبر البوت في أقرب وقت.</p>
              <textarea
                className="admin-input"
                rows="5"
                required
                value={supportMsg}
                onChange={(e) => setSupportMsg(e.target.value)}
                placeholder="اكتب تفاصيل مشكلتك هنا بوضوح..."
                style={{ width: '100%', resize: 'none', background: '#12141a', border: '1px solid #334155', color: '#fff', borderRadius: '10px', padding: '12px', fontSize: '0.9rem', fontFamily: 'inherit' }}
              />
              <button 
                type="submit" 
                disabled={submittingSupport || !supportMsg.trim()}
                className="btn-pay-stars-premium" 
                style={{ marginTop: '16px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', width: '100%', border: 'none', borderRadius: '12px', padding: '12px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}
              >
                {submittingSupport ? 'جاري إرسال الرسالة...' : 'إرسال تذكرة الدعم الفني'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* مودال اقتراح ميزة */}
      {showSuggestionModal && (
        <div className="vip-modal-overlay" onClick={() => setShowSuggestionModal(false)}>
          <div className="vip-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="vip-modal-header">
              <div className="vip-modal-title">
                <Lightbulb size={20} color="#eab308" />
                <h3>اقتراح ميزة جديدة</h3>
              </div>
              <button type="button" className="vip-modal-close" onClick={() => setShowSuggestionModal(false)}>×</button>
            </div>
            <form onSubmit={handleSendSuggestion} className="vip-modal-body" style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '12px' }}>نهتم دائماً بآراء مستخدمينا! إذا كانت لديك فكرة رائعة أو ميزة تود إضافتها في سرّك، شاركها معنا مباشرة.</p>
              <textarea
                className="admin-input"
                rows="5"
                required
                value={suggestionMsg}
                onChange={(e) => setSuggestionMsg(e.target.value)}
                placeholder="صف الفكرة أو الميزة التي تقترحها..."
                style={{ width: '100%', resize: 'none', background: '#12141a', border: '1px solid #334155', color: '#fff', borderRadius: '10px', padding: '12px', fontSize: '0.9rem', fontFamily: 'inherit' }}
              />
              <button 
                type="submit" 
                disabled={submittingSuggestion || !suggestionMsg.trim()}
                className="btn-pay-stars-premium" 
                style={{ marginTop: '16px', background: 'linear-gradient(135deg, #eab308, #ca8a04)', width: '100%', border: 'none', borderRadius: '12px', padding: '12px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}
              >
                {submittingSuggestion ? 'جاري تقديم الاقتراح...' : 'إرسال الاقتراح للإدارة'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* مودال تأكيد حذف الحساب */}
      {showDeleteModal && (
        <div className="vip-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="vip-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="vip-modal-header">
              <div className="vip-modal-title">
                <Trash2 size={20} color="#f87171" />
                <h3>حذف الحساب نهائياً</h3>
              </div>
              <button type="button" className="vip-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="vip-modal-body" style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.9rem', color: '#fca5a5', fontWeight: 'bold', marginBottom: '10px' }}>⚠️ تحذير: هذا الإجراء غير قابل للتراجع!</p>
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '20px' }}>
                عند حذف حسابك، سيتم إزالة جميع بياناتك الشخصية تماماً من قاعدة البيانات، بما في ذلك الصداقات الحالية، وسجل المحادثات، وجلسات الرابط المجهول ورسائلها، ولا يمكن استعادة هذه البيانات لاحقاً.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                  className="btn-pay-stars-premium"
                  style={{ flex: 1, background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', borderRadius: '12px', padding: '12px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}
                >
                  {deleteLoading ? 'جاري حذف الحساب...' : 'نعم، احذف حسابي'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  style={{ flex: 1, background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', padding: '12px', color: '#cbd5e1', fontWeight: '700', cursor: 'pointer' }}
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* مودال تفاصيل التذكرة والمتابعة والردود */}
      {activeTicket && (
        <div className="vip-modal-overlay" onClick={() => setActiveTicket(null)}>
          <div className="vip-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px', display: 'flex', flexDirection: 'column', maxHeight: '82vh', overflow: 'hidden' }}>
            <div className="vip-modal-header" style={{ paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <div className="vip-modal-title">
                <Wrench size={20} color="#3b82f6" />
                <h3>متابعة تذكرة الدعم الفني</h3>
              </div>
              <button type="button" className="vip-modal-close" onClick={() => setActiveTicket(null)}>×</button>
            </div>
            
            <div className="vip-modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px', minHeight: '220px', background: 'transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>حالة التذكرة: <b style={{ color: activeTicket.status === 'replied' ? '#10b981' : activeTicket.status === 'closed' ? '#ef4444' : '#eab308' }}>
                  {activeTicket.status === 'replied' ? 'تم الرد' : activeTicket.status === 'closed' ? 'مغلقة ومكتملة' : 'قيد الانتظار'}
                </b></span>
                {activeTicket.status !== 'closed' && (
                  <button type="button" onClick={() => handleCloseTicket(activeTicket._id)} style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#f87171', fontSize: '0.75rem', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>إغلاق التذكرة</button>
                )}
              </div>

              <div className="chat-messages-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(activeTicket.messages || []).map((m, idx) => (
                  <div key={idx} style={{ 
                    alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start', 
                    background: m.sender === 'user' ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'rgba(255, 255, 255, 0.05)', 
                    color: '#fff', 
                    padding: '10px 14px', 
                    borderRadius: m.sender === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0', 
                    maxWidth: '82%', 
                    textAlign: 'right', 
                    fontSize: '0.88rem',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{m.text}</p>
                    <span style={{ fontSize: '0.65rem', color: m.sender === 'user' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.4)', display: 'block', marginTop: '4px', textAlign: 'left' }}>
                      {new Date(m.createdAt).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {activeTicket.status !== 'closed' ? (
              <form onSubmit={handleSendTicketReply} style={{ padding: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', gap: '8px', background: 'rgba(255, 255, 255, 0.01)' }}>
                <input
                  type="text"
                  required
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="اكتب ردك ومتابعتك هنا..."
                  style={{ flex: 1, background: '#12141a', border: '1px solid #334155', color: '#fff', borderRadius: '10px', padding: '12px 14px', fontSize: '0.88rem', outline: 'none' }}
                />
                <button
                  type="submit"
                  disabled={submittingReply || !replyText.trim()}
                  style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', border: 'none', color: '#fff', padding: '0 20px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {submittingReply ? '...' : 'إرسال'}
                </button>
              </form>
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)', background: 'rgba(0, 0, 0, 0.1)' }}>
                🔒 تم إغلاق وتصفية هذه التذكرة بنجاح.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

